import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import * as Sentry from "@sentry/react";
import { AI_PAYWALL_ROUTE, APP_CONFIG, APP_TO_BUILD, ENV } from "../../../appsConfig";
import { claimReferralTrial, createCheckoutSession } from "@/api/fetch";
import RecoveryBanners from "@/components/common/RecoveryBanners";
import { getCurrentLanguage } from "@/i18n";
import { getPaywallCopy } from "@/i18n/paywallCopy";
import { cn } from "@/lib/utils";
import { showToastFromKey } from "@/lib/toast";
import { useRootStore } from "@/state/RootStore";
import { formatCurrency, isSupportedCurrency } from "@/utils/currency";
import { captureEvent } from "@/utils/posthog";
import { PaywallPlanCard, type PaywallFeature } from "./PaywallPlanCard";
import { CecPaywallPlanCard, type CecPlanId } from "./CecPaywallPlanCard";
import { DEFAULT_CEC_PLAN } from "./cecPlanFeatures";

interface PaywallProps {
  variant?: "default" | "ai";
}

function getFeatureIndexes(variant: "default" | "ai") {
  if (variant === "ai") return [0, 1, 2];
  switch (APP_TO_BUILD) {
    case "electrical_us":
      return [1, 0, 2, 3];
    case "plumbing":
      return [1, 3, 2, 4];
    default:
      return [0, 1, 2, 3];
  }
}

const Paywall = observer(({ variant = "default" }: PaywallProps) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || getCurrentLanguage();
  const aiSku = APP_CONFIG.proModeSkus[ENV].aiSub;
  const store = useRootStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState("");
  const [cecPlan, setCecPlan] = useState<CecPlanId>(DEFAULT_CEC_PLAN);
  const cancelRoute = variant === "ai" ? AI_PAYWALL_ROUTE : "/paywall";

  const isNecTrialEligible =
    APP_TO_BUILD === "electrical_us" &&
    variant === "default" &&
    store.mainAppStore.isStudyTrialEligible();
  const referralFlagEnabled = useFeatureFlagEnabled("refer-a-friend") === true;
  const referralCtaActive =
    referralFlagEnabled &&
    variant === "default" &&
    store.mainAppStore.isReferralOfferClaimable() &&
    store.mainAppStore.isReferralOfferDismissed();
  const referralCode = store.mainAppStore.referralOffer()?.code;
  const partialLevelAccess =
    (APP_TO_BUILD === "plumbing" || APP_TO_BUILD === "electrical") &&
    variant === "default" &&
    !!store.mainAppStore.hasStudyAccess(false) &&
    !store.mainAppStore.hasAllLevelStudyAccess();
  const trialOutcomeRef = useRef<"pending" | "initiated" | "dismissed">("pending");

  const captureTrialDismissed = useCallback(() => {
    if (!isNecTrialEligible || trialOutcomeRef.current !== "pending") return;
    trialOutcomeRef.current = "dismissed";
    captureEvent("trial_offer_dismissed", {
      platform: "web",
      appType: APP_TO_BUILD,
    });
  }, [isNecTrialEligible]);

  const handleOpenPurchase = useCallback(
    async (priceId: string, plan?: "monthly" | "annual") => {
      setIsLoading(priceId);
      if (isNecTrialEligible) {
        trialOutcomeRef.current = "initiated";
        captureEvent("trial_purchase_initiated", {
          platform: "web",
          appType: APP_TO_BUILD,
        });
      }
      captureEvent(
        variant === "ai" ? "web_ai_paywall_clicked" : "web_paywall_clicked",
        plan ? { plan } : undefined,
      );
      const currentUrl = window.location.origin;
      const link =
        import.meta.env.DEV || currentUrl.includes("localhost:")
          ? `${currentUrl}/${APP_TO_BUILD}`
          : currentUrl;
      const successParams = new URLSearchParams();
      if (variant === "ai") successParams.set("variant", "ai");
      if (plan) successParams.set("plan", plan);
      const successQuery = successParams.toString();
      const successPath = `/payment-success${successQuery ? `?${successQuery}` : ""}`;

      try {
        const response = (await createCheckoutSession({
          priceId,
          successUrl: `${link}${successPath}`,
          cancelUrl: `${link}${cancelRoute}`,
        })) as { url?: string };
        if (!response.url) throw new Error("createCheckoutSession returned no url");
        window.location.href = response.url;
      } catch (error) {
        Sentry.captureException(error);
        showToastFromKey({
          titleKey: "paywall.checkout_error_title",
          descriptionKey: "paywall.checkout_error_body",
          variant: "destructive",
        });
      } finally {
        setIsLoading("");
      }
    },
    [cancelRoute, isNecTrialEligible, variant],
  );

  const handleReferralClaim = useCallback(async () => {
    setIsLoading("referral-claim");
    try {
      await claimReferralTrial();
      await store.mainAppStore.checkPurchaseState();
      captureEvent("referral_claim_success", {
        referral_code: referralCode,
        platform: "web",
      });
      navigate("/study");
    } catch (error) {
      Sentry.captureException(error);
      captureEvent("referral_claim_fail", {
        referral_code: referralCode,
        platform: "web",
      });
      showToastFromKey({
        titleKey: "paywall.checkout_error_title",
        descriptionKey: "paywall.checkout_error_body",
        variant: "destructive",
      });
    } finally {
      setIsLoading("");
    }
  }, [navigate, referralCode, store.mainAppStore]);

  const handleDismiss = useCallback(() => {
    captureTrialDismissed();
    navigate("/study");
  }, [captureTrialDismissed, navigate]);

  useEffect(() => {
    captureEvent(
      variant === "ai" ? "web_ai_paywall_view" : "web_paywall_view",
      APP_TO_BUILD === "plumbing" ? { default_plan: "monthly" } : undefined,
    );
  }, [variant]);

  useEffect(
    () => () => {
      captureTrialDismissed();
    },
    [captureTrialDismissed],
  );

  const copy = useMemo(
    () =>
      getPaywallCopy(t, APP_TO_BUILD, {
        variant,
        isNecTrialEligible,
        partialLevelAccess,
      }),
    [t, variant, isNecTrialEligible, partialLevelAccess],
  );

  const { subProduct, apprenticeTrackProduct, subMonthlyProduct } = useMemo(() => {
    if (variant === "ai") {
      return {
        subProduct: store.mainAppStore.products.find((product) => product.id === aiSku),
        apprenticeTrackProduct: undefined,
        subMonthlyProduct: undefined,
      };
    }

    switch (APP_TO_BUILD) {
      case "electrical": {
        const sub = store.mainAppStore.products.find(
          (product) => product.id === APP_CONFIG.proModeSkus[ENV].sub,
        );
        const apprenticeTrack = store.mainAppStore.products.find(
          (product) => product.id === APP_CONFIG.proModeSkus[ENV].apprenticeTrack,
        );
        return {
          subProduct: partialLevelAccess ? apprenticeTrack : sub,
          apprenticeTrackProduct: apprenticeTrack,
          subMonthlyProduct: undefined,
        };
      }
      case "electrical_us":
      case "ibew_aptitude":
        return {
          subProduct: store.mainAppStore.products.find(
            (product) => product.id === APP_CONFIG.proModeSkus[ENV].sub,
          ),
          apprenticeTrackProduct: undefined,
          subMonthlyProduct: undefined,
        };
      case "plumbing":
        return {
          subProduct: store.mainAppStore.products.find(
            (product) => product.id === APP_CONFIG.proModeSkus[ENV].sub,
          ),
          apprenticeTrackProduct: undefined,
          subMonthlyProduct: store.mainAppStore.products.find(
            (product) => product.id === APP_CONFIG.proModeSkus[ENV].subMonthly,
          ),
        };
      default:
        return {
          subProduct: undefined,
          apprenticeTrackProduct: undefined,
          subMonthlyProduct: undefined,
        };
    }
  }, [aiSku, partialLevelAccess, store.mainAppStore.products, variant]);

  const plumbingMonthlyFlagEnabled =
    useFeatureFlagEnabled("plumbing-monthly-plan") === true;
  const annualCurrency = subProduct?.default_price?.currency;
  const monthlyCurrency = subMonthlyProduct?.default_price?.currency;
  const annualCurrencyCode = annualCurrency?.toUpperCase() ?? "";
  const monthlyCurrencyCode = monthlyCurrency?.toUpperCase() ?? "";
  const priceId = subProduct?.default_price?.id;
  const priceAmount = subProduct?.default_price?.unit_amount;
  const priceLoaded =
    !!priceId && typeof priceAmount === "number" && isSupportedCurrency(annualCurrency);
  const monthlyPriceId = subMonthlyProduct?.default_price?.id;
  const monthlyPriceAmount = subMonthlyProduct?.default_price?.unit_amount;
  const monthlyPriceLoaded =
    !!monthlyPriceId &&
    typeof monthlyPriceAmount === "number" &&
    isSupportedCurrency(monthlyCurrency);
  const plumbingMonthlyEnabled =
    APP_TO_BUILD === "plumbing" &&
    plumbingMonthlyFlagEnabled &&
    !!APP_CONFIG.proModeSkus[ENV].subMonthly;
  const showPlumbingTwoButton = plumbingMonthlyEnabled && monthlyPriceLoaded;

  const displayItems =
    APP_TO_BUILD === "plumbing" && !plumbingMonthlyEnabled && copy.items.length
      ? copy.items.map((item, index) =>
          index === copy.items.length - 1
            ? {
                title: t("paywall.plumbing_item5_title_annual_only"),
                description: t("paywall.plumbing_item5_description_annual_only"),
              }
            : item,
        )
      : copy.items;
  const planFeatures: Array<string | PaywallFeature> =
    APP_TO_BUILD === "plumbing" && variant === "default"
      ? [
          t("paywall.plumbing_coverage"),
          t("paywall.plumbing_weak_area"),
          t("paywall.plumbing_questions"),
          t("paywall.plumbing_explanations"),
          { label: t("paywall.plumbing_ai_tutor"), value: 3 },
        ]
      : APP_TO_BUILD === "electrical_us" && variant === "default"
      ? [
          t("paywall.comparison_ai_tutor"),
          t("paywall.comparison_questions"),
          t("paywall.comparison_modes"),
          t("paywall.comparison_explanations"),
        ]
      : getFeatureIndexes(variant)
          .map((index) => displayItems[index]?.title)
          .filter((feature): feature is string => Boolean(feature));
  if (variant === "ai" && !aiSku) return <Navigate to="/paywall" replace />;

  const headlineAccent =
    variant === "ai"
      ? t("paywall.headline_ai")
      : APP_TO_BUILD === "electrical_us"
        ? t("paywall.headline_nec")
        : APP_TO_BUILD === "electrical"
          ? t("paywall.headline_cec")
          : APP_TO_BUILD === "plumbing"
            ? t("paywall.headline_plumbing")
            : t("paywall.headline_ibew");
  const formattedAnnualPrice = priceLoaded
    ? formatCurrency(priceAmount / 100, currentLanguage, annualCurrencyCode)
    : copy.loadingPrice;
  const billingLabel =
    variant === "ai" ? t("paywall.billed_monthly") : t("paywall.billed_annually");
  const isPrimaryLoading = isLoading === priceId || isLoading === "referral-claim";
  const primaryLabel = referralCtaActive
    ? isLoading === "referral-claim"
      ? copy.loading
      : t("referral.paywall.start_free_trial")
    : isLoading === priceId
      ? copy.loading
      : !priceLoaded
        ? copy.loadingPrice
        : isNecTrialEligible
          ? t("paywall.start_free_trial_button")
          : t("paywall.start_with_plan", { plan: copy.topLabel });
  const primaryDisabled = (!referralCtaActive && !priceLoaded) || Boolean(isLoading);
  const apprenticePrice = apprenticeTrackProduct?.default_price;
  const apprenticeCurrency = apprenticePrice?.currency;
  const apprenticePriceLoaded =
    Boolean(apprenticePrice?.id) &&
    typeof apprenticePrice?.unit_amount === "number" &&
    isSupportedCurrency(apprenticeCurrency);
  const formattedApprenticePrice = apprenticePriceLoaded
    ? formatCurrency(
        apprenticePrice!.unit_amount / 100,
        currentLanguage,
        apprenticeCurrency!.toUpperCase(),
      )
    : copy.loadingPrice;
  const hasConfiguredApprenticePlan = Boolean(
    APP_CONFIG.proModeSkus[ENV].apprenticeTrack,
  );
  const effectiveCecPlan = hasConfiguredApprenticePlan ? cecPlan : "annual";
  const selectedCecPriceId =
    effectiveCecPlan === "apprentice" ? apprenticePrice?.id : priceId;
  const selectedCecPriceLoaded =
    effectiveCecPlan === "apprentice" ? apprenticePriceLoaded : priceLoaded;
  const cecFeatures = [
    { annualIncluded: true, label: t("paywall.cec_coverage"), freeIncluded: true },
    { annualIncluded: true, label: t("paywall.cec_questions") },
    { annualIncluded: true, label: t("paywall.cec_modes") },
    { annualIncluded: true, label: t("paywall.cec_explanations") },
    { annualIncluded: false, label: t("paywall.cec_all_levels") },
  ];

  const primaryActions = showPlumbingTwoButton ? (
    <div className="flex gap-3">
      <button
        type="button"
        data-testid="paywall-monthly-button"
        onClick={() => handleOpenPurchase(monthlyPriceId!, "monthly")}
        disabled={!monthlyPriceLoaded || Boolean(isLoading)}
        className={cn(
          "h-14 w-full rounded-[28px] bg-[var(--dp-brand-button)] px-5 text-[16px] font-semibold text-white transition-colors hover:bg-[var(--dp-brand-hover)] disabled:bg-[#dde1df] disabled:text-[#8b928e]",
          isLoading === monthlyPriceId && "bg-[#dde1df] text-[#8b928e]",
        )}
      >
        {isLoading === monthlyPriceId
          ? copy.loading
          : t("paywall.continue_with_monthly_price", {
              price: formatCurrency(
                monthlyPriceAmount! / 100,
                currentLanguage,
                monthlyCurrencyCode,
              ),
            })}
      </button>
      <button
        type="button"
        data-testid="paywall-annual-button"
        onClick={() => handleOpenPurchase(priceId!, "annual")}
        disabled={!priceLoaded || Boolean(isLoading)}
        className={cn(
          "h-14 w-full rounded-[28px] bg-[var(--dp-brand-button)] px-5 text-[16px] font-semibold text-white transition-colors hover:bg-[var(--dp-brand-hover)] disabled:bg-[#dde1df] disabled:text-[#8b928e]",
          isLoading === priceId && "bg-[#dde1df] text-[#8b928e]",
        )}
      >
        {isLoading === priceId
          ? copy.loading
          : t("paywall.continue_with_annual_price", { price: formattedAnnualPrice })}
      </button>
    </div>
  ) : (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        data-testid="paywall-primary-button"
        onClick={() =>
          referralCtaActive ? handleReferralClaim() : handleOpenPurchase(priceId!)
        }
        disabled={primaryDisabled}
        className={cn(
          "h-14 w-full rounded-[28px] bg-[var(--dp-brand-button)] px-8 text-[18px] font-semibold leading-7 text-white transition-colors hover:bg-[var(--dp-brand-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand)] focus-visible:ring-offset-2 disabled:bg-[#dde1df] disabled:text-[#8b928e]",
          isPrimaryLoading && "bg-[#dde1df] text-[#8b928e]",
        )}
      >
        {primaryLabel}
      </button>

      {APP_TO_BUILD === "electrical" &&
        !partialLevelAccess &&
        apprenticeTrackProduct && (
          <button
            type="button"
            onClick={() => handleOpenPurchase(apprenticePrice!.id)}
            disabled={!apprenticePriceLoaded || Boolean(isLoading)}
            className="h-14 w-full rounded-[28px] border-[1.5px] border-[var(--dp-brand-button)] bg-white px-8 text-[16px] font-semibold text-[var(--dp-brand-button)] transition-colors hover:bg-[var(--dp-brand-soft)] disabled:border-[#c7ccc9] disabled:text-[#8b928e]"
          >
            {isLoading === apprenticePrice?.id
              ? copy.loading
              : apprenticePriceLoaded
                ? t("paywall.continue_apprentice_track", {
                    price: formatCurrency(
                      apprenticePrice.unit_amount / 100,
                      currentLanguage,
                      apprenticeCurrency!.toUpperCase(),
                    ),
                  })
                : copy.loadingPrice}
          </button>
        )}
    </div>
  );

  const backToHomeAction = (
    <button
      className="h-14 w-full rounded-[28px] border-[1.5px] border-[var(--dp-brand-button)] bg-transparent px-8 text-[18px] font-semibold text-[var(--dp-brand-button)] transition-colors hover:bg-[var(--dp-brand-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand)] focus-visible:ring-offset-2"
      data-testid="paywall-back-home-button"
      onClick={handleDismiss}
      type="button"
    >
      {t("paywall.back_to_home")}
    </button>
  );

  const cecPrimaryAction = (
    <div className="flex flex-col gap-3">
      <button
        className="dp-button-primary h-14 w-full px-8 text-lg"
        data-testid="paywall-primary-button"
        disabled={!selectedCecPriceLoaded || Boolean(isLoading)}
        onClick={() => selectedCecPriceId && handleOpenPurchase(selectedCecPriceId)}
        type="button"
      >
        {isLoading === selectedCecPriceId ? copy.loading : t("paywall.start_with_plan", { plan: t("paywall.label_pro") })}
      </button>
      {backToHomeAction}
    </div>
  );

  const headlineLead =
    APP_TO_BUILD === "plumbing" && variant === "default"
      ? t("paywall.headline_lead_plumbing")
      : APP_TO_BUILD === "electrical" && variant === "default"
        ? t("paywall.headline_lead_cec")
        : t("paywall.headline_lead");
  const showCecComparison =
    APP_TO_BUILD === "electrical" && variant === "default" && !partialLevelAccess;

  return (
    <div className="size-full overflow-y-auto" style={{ background: "var(--dp-paywall-background)" }}>
      <RecoveryBanners />
      <main className="mx-auto flex min-h-full w-full max-w-[1128px] items-center justify-center px-6 py-6">
        <div className="flex min-h-[704px] w-full max-w-[680px] min-w-0 flex-col items-center justify-center gap-8">
          <h1 className="w-full text-center text-[36px] font-bold leading-[normal] tracking-[-0.09px] text-black">
            <span className="block">{headlineLead}</span>
            <span className="block text-[var(--dp-brand)]">{headlineAccent}</span>
          </h1>
          {showCecComparison ? (
            <CecPaywallPlanCard
              actions={cecPrimaryAction}
              annualBillingLabel={t("paywall.billed_annually")}
              annualPlanLabel={t("paywall.cec_yearly")}
              annualPrice={formattedAnnualPrice}
              apprenticeBillingLabel={t("paywall.cec_for_four_years")}
              apprenticeColumnLabel={t("paywall.cec_apprentice")}
              apprenticePlanLabel={t("paywall.cec_apprentice_track")}
              apprenticePrice={formattedApprenticePrice}
              features={cecFeatures}
              freeLabel={t("paywall.free_plan")}
              onSelectPlan={setCecPlan}
              savingsLabel={t("paywall.cec_save_amount")}
              selectedPlan={effectiveCecPlan}
            />
          ) : (
            <PaywallPlanCard
              planName={copy.topLabel}
              price={formattedAnnualPrice}
              billingLabel={billingLabel}
              includedAiLabel={t("paywall.includes_ai")}
              freeLabel={t("paywall.free_plan")}
              features={planFeatures}
              actions={primaryActions}
              secondaryAction={backToHomeAction}
            />
          )}
        </div>
      </main>
    </div>
  );
});

export default Paywall;
